import React from 'react';

export type AvatarKind = 'ai' | 'human';
/** Live task-execution status dot (working/available/blocked/paused). */
export type AvatarStatus = 'working' | 'available' | 'blocked' | 'paused';
/** Durable org lifecycle status dot — NOT project activity. Mutually exclusive with `status` in practice. */
export type AvatarLifecycle = 'active' | 'paused' | 'needs_setup' | 'terminated';
/** `'sm' | 'md'` presets, or an exact pixel size (workforce surfaces pass 26–56px explicitly). */
export type AvatarSize = 'sm' | 'md' | number;

/**
 * Avatar — per comp .avatar. Single implementation (QG-CLI-COMP-011): this
 * used to be duplicated as `pages/workforce/shared/Avatar.tsx`, with drifted
 * initials tie-break logic (last word here vs. second word there) and no
 * empty-label guard here. That copy is gone; this one now covers both call
 * shapes.
 *
 * - kind="human" → indigo-to-blue gradient
 * - kind="ai"    → accent-to-purple gradient
 * - Renders explicit `initials`, or derives them from `label` (up to 2 chars),
 *   or an icon.
 * - Optional `status` dot (live task-execution) OR `lifecycle` dot (durable
 *   org lifecycle) — right-bottom, per comp .status-dot variants.
 * - Optional small kind badge (AI/H) in the top-left corner, per the
 *   workforce roster comp — opt in via `showKind`.
 */
interface AvatarProps {
  kind: AvatarKind;
  /** Display name — initials are derived from this unless `initials` is given. */
  label?: string;
  /** Explicit initials override (skips derivation from `label`). */
  initials?: string;
  /** Override initials/derivation with a custom icon (16px lucide-react recommended) */
  icon?: React.ReactNode;
  size?: AvatarSize;
  status?: AvatarStatus;
  lifecycle?: AvatarLifecycle;
  /** Small AI/H kind badge, top-left corner. Default false. */
  showKind?: boolean;
  className?: string;
}

/**
 * Derive up to 2-char initials from a label string. Last-word tie-break
 * (e.g. "Ada B. Lovelace" → "AL") and a '?' guard for an empty/whitespace-only
 * label — adopted from the former workforce copy, which was the more correct
 * of the two.
 */
function deriveInitials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const sizePresetClasses: Record<'sm' | 'md', string> = {
  sm: 'w-[26px] h-[26px] text-[10px] rounded-[7px]',
  md: 'w-8 h-8 text-[12px] rounded-[9px]',
};

/** Status dot background color — semantic classes */
const statusDotClasses: Record<AvatarStatus, string> = {
  working: 'bg-accent',
  available: 'bg-success',
  blocked: 'bg-danger',
  paused: 'bg-warning',
};

/** Lifecycle → status-dot fill. Mirrors LIFECYCLE_META (workforce/shared/bench-model.ts). */
const lifecycleDotClasses: Record<AvatarLifecycle, string> = {
  active: 'bg-success',
  paused: 'bg-warning',
  needs_setup: 'bg-purple',
  terminated: 'bg-faint',
};

/**
 * Gradient backgrounds via Tailwind gradient utilities.
 *
 * "human" uses indigo-600→blue-600. Indigo/blue are decorative accent colors
 * (not semantic UI-role colors) so Tailwind raw color utilities are permitted
 * per client CLAUDE.md §Styling ("decorative colors like category badges…
 * that don't have semantic meaning").
 *
 * "ai" uses accent→purple, both exposed as semantic CSS vars.
 */
const kindGradient: Record<AvatarKind, string> = {
  human: 'bg-linear-to-br from-indigo-600 to-blue-600',
  ai: 'bg-linear-to-br from-accent to-purple',
};

export const Avatar: React.FC<AvatarProps> = ({
  kind,
  label,
  initials,
  icon,
  size = 'md',
  status,
  lifecycle,
  showKind = false,
  className = '',
}) => {
  const isNumericSize = typeof size === 'number';
  const dotSize = isNumericSize ? Math.max(8, Math.round(size * 0.22)) : size === 'sm' ? 8 : 10;
  const kindBadgeSize = isNumericSize ? Math.max(11, Math.round(size * 0.28)) : 12;
  const resolvedInitials = initials ?? (label ? deriveInitials(label) : undefined);

  return (
    <span
      className={`inline-grid place-items-center font-semibold shrink-0 text-white relative ${
        isNumericSize ? '' : sizePresetClasses[size]
      } ${kindGradient[kind]} ${className}`}
      style={isNumericSize ? { width: size, height: size, borderRadius: size >= 48 ? 14 : 9 } : undefined}
      aria-label={label}
      title={label}
    >
      {icon ? (
        <span className="[&>svg]:w-4 [&>svg]:h-4" aria-hidden="true">
          {icon}
        </span>
      ) : (
        resolvedInitials
      )}

      {showKind && (
        <span
          className={`absolute left-[-3px] top-[-3px] inline-flex items-center justify-center rounded-full border-2 border-bg font-bold text-white ${
            kind === 'ai' ? 'bg-purple' : 'bg-success'
          }`}
          style={{ width: kindBadgeSize, height: kindBadgeSize, fontSize: Math.round(kindBadgeSize * 0.55) }}
          aria-hidden="true"
        >
          {kind === 'ai' ? 'AI' : 'H'}
        </span>
      )}

      {status && (
        <span
          className={`absolute -right-0.5 -bottom-0.5 rounded-full border-2 border-bg ${statusDotClasses[status]}`}
          style={{ width: dotSize, height: dotSize }}
          aria-label={status}
        />
      )}
      {lifecycle && (
        <span
          className={`absolute -right-0.5 -bottom-0.5 rounded-full border-2 border-bg ${lifecycleDotClasses[lifecycle]}`}
          style={{ width: dotSize, height: dotSize }}
          aria-hidden="true"
        />
      )}
    </span>
  );
};
